import { type SelectProps } from '@jouzen/ecom-components/v3/Select';
import { useTranslations } from 'next-intl';
import { useMemo } from 'react';

import { useCountriesList, useCountryRegions } from '@/queries/Countries';

export interface CountryData {
  countryOptions: SelectProps['options'];
  hasRegions: boolean;
  isError: boolean;
  isLoading: boolean;
  regionOptions: SelectProps['options'];
}

const useCountryData = (countryCode: string): CountryData => {
  const t = useTranslations();

  const {
    data: countriesList,
    isError: countriesError,
    isLoading: countriesLoading,
  } = useCountriesList();

  const {
    data: regions,
    isError: regionsError,
    isLoading: regionsLoading,
  } = useCountryRegions(countryCode);

  const countryOptions = useMemo(
    () =>
      countriesList
        .map((country) => ({
          label: country.labelMsgKey ? t(country.labelMsgKey) : country.name,
          value: country.countryCode,
        }))
        .sort((a, b) => a.label.localeCompare(b.label)),
    [countriesList, t],
  );

  const regionOptions = useMemo(
    () =>
      regions
        .map((region) => ({
          label: region.labelMsgKey ? t(region.labelMsgKey) : region.label,
          value: region.value,
        }))
        .sort((a, b) => a.label.localeCompare(b.label)),
    [regions, t],
  );

  return {
    countryOptions,
    hasRegions: regions.length > 0,
    isError: countriesError || regionsError,
    isLoading:
      (!countriesList.length && countriesLoading) ||
      (!regions.length && regionsLoading),
    regionOptions,
  };
};

export default useCountryData;
